import { AgGridReact } from 'ag-grid-react';
import { LicenseManager } from 'ag-grid-enterprise';
import { AG_GRID_LICENSE } from '../../constants';
import { useMemo, useCallback, useEffect } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {
  StyledTableWrapper,
  StyledTableContainer,
  StyledDataCellText,
} from './StyledComponents';
import { CONTENT_TYPES } from 'cdk-radial';
import { IPRestrictionData } from './IPRestrictionInterface';
import { useTranslation } from 'react-i18next';
import { dateValueFormatter } from './FeatureAccessContainer';

LicenseManager.setLicenseKey(AG_GRID_LICENSE);

const CustomRenderer = ({
  isParentApp,
  value,
}: {
  isParentApp: boolean;
  value: string[];
}) => {
  const contentType = isParentApp ? CONTENT_TYPES.BODY_2 : CONTENT_TYPES.BODY_1;
  return (
    <StyledDataCellText
      type={contentType}
      lineHeight={isParentApp ? '46px' : '20px'}
    >
      {!value[1] ? 'Unify' : isParentApp ? value[1] : value[2]}
    </StyledDataCellText>
  );
};

const CustomIPAddressRenderer = ({
  isParentApp,
  isUnifyAppHasValue,
  isParentAppHasValue,
  value,
  rowIndex,
}: {
  isParentApp: boolean;
  isUnifyAppHasValue: boolean;
  isParentAppHasValue: boolean;
  value: string[];
  rowIndex: number;
}) => {
  const contentType = isParentApp ? CONTENT_TYPES.BODY_2 : CONTENT_TYPES.BODY_1;
  return (
    <StyledDataCellText type={contentType}>
      {value?.map((ip: any, index: number) => (
        <div
          key={index}
          style={{
            color: (isUnifyAppHasValue || (!isParentApp && isParentAppHasValue)) && rowIndex !== 0 ? 'gray' : 'inherit',
          }}
        >
          {ip}
        </div>
      ))}
    </StyledDataCellText>
  );
};

const CustomIPRangeRenderer = ({
  isParentApp,
  isUnifyAppHasValue,
  isParentAppHasValue,
  value,
  rowIndex,
}: {
  isParentApp: boolean;
  isUnifyAppHasValue: boolean;
  isParentAppHasValue: boolean;
  value: string[];
  rowIndex: number;
}) => {
  const contentType = isParentApp ? CONTENT_TYPES.BODY_2 : CONTENT_TYPES.BODY_1;
  return (
    <StyledDataCellText type={contentType}>
      {value?.map((range: any, index: number) => (
        <div
          key={index}
          style={{
            color: (isUnifyAppHasValue || (!isParentApp && isParentAppHasValue)) && rowIndex !== 0 ? 'gray' : 'inherit',
          }}
        >
          {range.begin} - {range.end}
        </div>
      ))}
    </StyledDataCellText>
  );
};



const getIndentClass = (params: any) => {
  var indent = 0;
  var node = params.node;
  while (node && node.parent) {
    indent++;
    node = node.parent;
  }
  return 'indent-' + indent;
};

const defaultColDef = {
  enablePivot: true,
  sortable: true,
  filter: true,
  flex: 1,
  resizable: true,
  refreshCells: true,
};

export default function IPRestrictionAGGrid({
  rowData,
  onSelectRow,
  gridRef
}: {
  rowData: IPRestrictionData[];
  onSelectRow: Function;
  gridRef: any
}) {
  const { t } = useTranslation();
  const getDataPath = useCallback((data: any) => data.name, []);

  useEffect(()=>{
    gridRef.current?.api?.forEachNode((node: any) => {
      if (node.key === 'Unify') {
        node.setExpanded(true);
      }
    });

  },[rowData,gridRef]);

  const onGridReady = (params: any) => {
    params.api.forEachNode((node: any) => {
      if (node.key === 'Unify') {
        node.setExpanded(true);
      }
    });
  };

  const colDefs = [
    {
      field: 'allowedIps',
      headerName: t('dealer-admin.ip-access.label.allowedIPAddress') as string,
      cellClass: 'formattedColumn',
      autoHeight: true,
      cellRenderer: (params: any) => {
        const unifyNode = params.api.getDisplayedRowAtIndex(0);
        const allowedIpRangeCheck = unifyNode?.data?.allowedIpRange?.length > 0;
        const allowedIps = unifyNode?.data?.allowedIps?.join(',').length > 0;

        const appIpRange = params?.node?.parent?.data?.allowedIpRange?.length > 0;
        const appAllowedIps = params?.node?.parent?.data?.allowedIps?.join(',').length > 0;

        const templateAllowedIps = params?.data?.availableTemplates
            .filter((item:any) => params?.data?.templates?.includes(item.id))
            .map((item:any) => item.allowedIps);
        
        const allIps = [...templateAllowedIps, ...params.data.allowedIps];

        return (
          <CustomIPAddressRenderer
            isParentApp={params.data.type === 'application'}
            isUnifyAppHasValue={allowedIps || allowedIpRangeCheck}
            isParentAppHasValue={appAllowedIps || appIpRange}
            value={allIps}
            rowIndex={params.rowIndex}
          />
        );
      },
    },
    {
      field: 'allowedIpRange',
      headerName: t('dealer-admin.ip-access.label.allowedIPRange') as string,
      cellClass: 'formattedColumn',
      autoHeight: true,
      cellRenderer: (params: any) => {
        const unifyNode = params.api.getDisplayedRowAtIndex(0);
        const allowedIpRangeCheck = unifyNode?.data?.allowedIpRange?.length > 0;
        const allowedIps = unifyNode?.data?.allowedIps?.join(',').length > 0;

        const appIpRange = params?.node?.parent?.data?.allowedIpRange?.length > 0;
        const appAllowedIps = params?.node?.parent?.data?.allowedIps?.join(',').length > 0;

        const templateAllowedRangesIps = params?.data?.availableTemplates
            .filter((item:any) => params?.data?.templates?.includes(item.id))
            .map((item:any) => item.allowedIpRange);

        const allIps = [...templateAllowedRangesIps, ...params.data.allowedIpRange]?.flat();

        return (
          <CustomIPRangeRenderer
            isParentApp={params.data.type === 'application'}
            isUnifyAppHasValue={allowedIps || allowedIpRangeCheck}
            isParentAppHasValue={appAllowedIps || appIpRange}
            value={allIps}
            rowIndex={params.rowIndex}
          />
        );
      },
    },
    {
        field: 'updatedBy',
        cellClass: 'formattedColumn',
        headerName: t('dealer-admin.ip-access.label.updatedBy'),
        sortable: true
    },
    {
        field: 'updatedOn',
        cellClass: 'formattedColumn',
        headerName: t('dealer-admin.ip-access.label.updatedOn'),
        cellRenderer: (params: any) => (
            <>{dateValueFormatter(params.data.updatedOn)}</>
        ),
        minWidth: 180,
        sortable: false
    },
  ];

  const handleRowSelection = (params: any) => {
    const unifyNode = params.api.getDisplayedRowAtIndex(0);
    const allowedIpRangeCheck = unifyNode?.data?.allowedIpRange?.length > 0;
    const allowedIps = unifyNode?.data?.allowedIps?.join(',').length > 0;
    const unifyAppHasValue = allowedIps || allowedIpRangeCheck;

    const appIpRange = params?.node?.parent?.data?.allowedIpRange?.length > 0;
    const appAllowedIps = params?.node?.parent?.data?.allowedIps?.join(',').length > 0;

    const isParentAppHasValue = (appIpRange || appAllowedIps);

    if ((!unifyAppHasValue || params.rowIndex === 0) && !isParentAppHasValue) {
      onSelectRow(params.data);
    }
  };

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: t('dealer-admin.ip-access.label.appName') as string,
      cellClass: getIndentClass,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: (params: any) => (
          <CustomRenderer
            isParentApp={params.data.type === 'application'}
            value={params.data.name}
          />
        ),
      },
      sortable: true,
      comparator: (
        valueA: any,
        valueB: any,
        nodeA: any,
        nodeB: any,
        isDescending: any
      ) => {
        if (
          nodeA.data.name[1].toLowerCase() === nodeB.data.name[1].toLowerCase()
        )
          return 0;
        return nodeA.data.name[1].toLowerCase() >
          nodeB.data.name[1].toLowerCase()
          ? 1
          : -1;
      },
    };
  }, [t]);

  return (
    <StyledTableContainer>
      <StyledTableWrapper className="ag-theme-alpine">
        <AgGridReact
          treeData={true}
          rowData={rowData}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          getDataPath={getDataPath}
          autoGroupColumnDef={autoGroupColumnDef}
          animateRows={true}
          rowSelection="single"
          onCellClicked={handleRowSelection}
          domLayout="autoHeight"
          suppressHorizontalScroll={true}
          suppressRowVirtualisation={true}
          onGridReady={onGridReady}
          ref={gridRef}
        />
      </StyledTableWrapper>
    </StyledTableContainer>
  );
}
